<template>
  <div v-if="userEmail" class="floating-chat-container" :style="containerStyle">
    <div ref="buttonContainerRef">
      <v-btn
        fab
        color="primary"
        class="floating-chat-button"
        @click.prevent="handleButtonClick"
        @mousedown="startDrag"
        large
      >
        <v-icon>mdi-chat</v-icon>
      </v-btn>
    </div>
    <div
      class="chat-popup-manual"
      :class="popupPositionClass"
      v-show="open"
      ref="popupRef"
    >
      <div class="chat-popup-content">
        <div class="chat-interface">
          <div style="width: 100%; height: 100%">
            <iframe
              :src="iframeUrl"
              style="border: none"
              title="AI Chat"
              allow="clipboard-write"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingChatButton',
  data() {
    return {
      open: false,
      userEmail: '',
      position: { x: 'right', y: 'bottom' }, // 'left' | 'right' for x, 'top' | 'bottom' for y
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      currentOffset: { x: 0, y: 0 },
      hasMoved: false, // Track if mouse has moved significantly
    };
  },
  computed: {
    iframeUrl() {
      return `https://ai.eduracle.com/?isInternalAppLogin=true&platform=external${this.userEmail ? `&email=${encodeURIComponent(this.userEmail)}` : ''}`;
    },
    containerStyle() {
      const style = {
        position: 'fixed',
        zIndex: 1000,
      };
      
      if (this.position.x === 'left') {
        style.left = `${20 + this.currentOffset.x}px`;
        style.right = 'auto';
      } else {
        style.right = `${20 - this.currentOffset.x}px`;
        style.left = 'auto';
      }
      
      if (this.position.y === 'top') {
        style.top = `${20 + this.currentOffset.y}px`;
        style.bottom = 'auto';
      } else {
        style.bottom = `${20 - this.currentOffset.y}px`;
        style.top = 'auto';
      }
      
      return style;
    },
    popupPositionClass() {
      return {
        'popup-top-left': this.position.y === 'top' && this.position.x === 'left',
        'popup-top-right': this.position.y === 'top' && this.position.x === 'right',
        'popup-bottom-left': this.position.y === 'bottom' && this.position.x === 'left',
        'popup-bottom-right': this.position.y === 'bottom' && this.position.x === 'right',
      };
    },
  },
  mounted() {
    // Get user email from sessionStorage (Vue app uses 'user' key)
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      this.userEmail = user && (user.email || user.username || '');
    } catch (e) {
      this.userEmail = '';
    }
    
    // Load saved position from localStorage
    this.loadPosition();
    
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.endDrag);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.endDrag);
  },
  methods: {
    loadPosition() {
      try {
        const saved = localStorage.getItem('floatingChatButtonPosition');
        if (saved) {
          const parsed = JSON.parse(saved);
          this.position = parsed.position || this.position;
          this.currentOffset = parsed.currentOffset || { x: 0, y: 0 };
        }
      } catch (e) {
        // Use default position
      }
    },
    savePosition() {
      try {
        localStorage.setItem('floatingChatButtonPosition', JSON.stringify({
          position: this.position,
          currentOffset: this.currentOffset,
        }));
      } catch (e) {
        // Ignore storage errors
      }
    },
    startDrag(event) {
      // Only start drag if it's not a click (allow small movements)
      if (event.button !== 0) return; // Only left mouse button
      
      this.hasMoved = false;
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX - this.currentOffset.x,
        y: event.clientY - this.currentOffset.y,
      };
      
      // Prevent text selection during drag
      event.preventDefault();
    },
    onDrag(event) {
      if (!this.isDragging) return;
      
      const deltaX = event.clientX - this.dragStart.x;
      const deltaY = event.clientY - this.dragStart.y;
      
      // Check if mouse has moved significantly (more than 5px)
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > 5) {
        this.hasMoved = true;
      }
      
      this.currentOffset = { x: deltaX, y: deltaY };
    },
    endDrag(event) {
      if (!this.isDragging) return;
      
      const wasDragging = this.hasMoved;
      this.isDragging = false;
      
      // If user didn't drag significantly, treat it as a click
      if (!wasDragging) {
        this.currentOffset = { x: 0, y: 0 };
        // Small delay to allow click event to fire
        setTimeout(() => {
          this.hasMoved = false;
        }, 0);
        return;
      }
      
      // Determine which corner to snap to
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const buttonSize = 60; // Button width/height
      const margin = 20;
      
      // Get the button's current bounding rect to find absolute position
      const buttonRect = this.$refs.buttonContainerRef.getBoundingClientRect();
      const centerX = buttonRect.left + buttonRect.width / 2;
      const centerY = buttonRect.top + buttonRect.height / 2;
      
      // Determine nearest corner
      const isLeft = centerX < windowWidth / 2;
      const isTop = centerY < windowHeight / 2;
      
      // Update position and reset offset
      this.position = {
        x: isLeft ? 'left' : 'right',
        y: isTop ? 'top' : 'bottom',
      };
      this.currentOffset = { x: 0, y: 0 };
      
      // Save position
      this.savePosition();
    },
    handleButtonClick(event) {
      // Only toggle if user didn't drag
      if (!this.hasMoved) {
        this.togglePopup();
      }
    },
    togglePopup() {
      this.open = !this.open;
    },
    handleClickOutside(event) {
      if (!this.open) return;
      const popup = this.$refs.popupRef;
      const button = this.$refs.buttonContainerRef;
      if (
        popup && !popup.contains(event.target) &&
        button && !button.contains(event.target)
      ) {
        this.open = false;
      }
    },
  },
};
</script>

<style scoped>
@media (max-width: 600px) {
  .chat-popup-manual {
    width: 95vw;
    height: 70vh;
  }
  
  .chat-popup-manual.popup-bottom-right {
    right: 2vw;
    bottom: 90px;
    left: auto;
    top: auto;
  }
  
  .chat-popup-manual.popup-bottom-left {
    left: 2vw;
    bottom: 90px;
    right: auto;
    top: auto;
  }
  
  .chat-popup-manual.popup-top-right {
    right: 2vw;
    top: 90px;
    left: auto;
    bottom: auto;
  }
  
  .chat-popup-manual.popup-top-left {
    left: 2vw;
    top: 90px;
    right: auto;
    bottom: auto;
  }
}


.chat-interface{
  height: 550px;
  width: 970px;
}
.chat-interface iframe{
  height: 100% !important;
  width: 100% !important;
}

.floating-chat-container {
  z-index: 1000;
  cursor: move;
  user-select: none;
}

.floating-chat-button {
  width: 60px !important;
  height: 60px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #6ea7f2, #303f9f) !important;
  cursor: grab;
}

.floating-chat-button:active {
  cursor: grabbing;
}

.floating-chat-button i.icon {
  font-size: 24px;
  margin: 0 !important;
  color: #fff !important;
  pointer-events: none;
}

.chat-popup-manual {
  position: absolute;
  width: 1000px;
  background-color: #f2f3f3;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  font-family: MyriadProRegular;
  border: 1px solid rgba(110, 167, 242, 0.3);
}

.chat-popup-manual.popup-bottom-right {
  bottom: 70px;
  right: 0;
  left: auto;
  top: auto;
}

.chat-popup-manual.popup-bottom-left {
  bottom: 70px;
  left: 0;
  right: auto;
  top: auto;
}

.chat-popup-manual.popup-top-right {
  top: 70px;
  right: 0;
  left: auto;
  bottom: auto;
}

.chat-popup-manual.popup-top-left {
  top: 70px;
  left: 0;
  right: auto;
  bottom: auto;
}

.chat-popup-content {
  padding: 15px;
  background-image: linear-gradient(to right bottom, rgba(110, 167, 242, 0.05), rgba(110, 237, 209, 0.05));
  border-radius: 8px;
  background: #fff;
}


@keyframes blink {
  50% {
    opacity: 1;
  }
}
</style> 